import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  Switch,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box,
  Typography,
  Alert,
  Avatar,
  IconButton,
  Collapse,
  Chip,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { NoticeboardButton, SearchField, ActionModal } from "../shared";
import { api } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { getAvatarColor, getInitials } from "../../utils/avatarUtils";
import { lightTheme } from "../../styles/theme";
import { phoneMatchesSearch } from "../../utils/phoneUtils";
import ProfileImageSelectorModal from "../ProfileImageSelectorModal";
import { getMaxSubcircles, isUnlimitedTierLimit } from "../../utils/tierLimits";
import {
  CIRCLE_DESCRIPTION_MAX_LENGTH,
  CIRCLE_LEARNING_POINT_MAX,
  CIRCLE_LEARNING_POINT_OPTIONS,
  DESCRIPTION_HELPER_TEXT,
  DESCRIPTION_MAX_LENGTH_MESSAGE,
  DESCRIPTION_REQUIRED_MESSAGE,
  LEARNING_POINTS_HELPER_TEXT,
  LEARNING_POINTS_REQUIRED_MESSAGE,
} from "./learningPointOptions";
import type { CreateCircleFormFooterState } from "./circleFormFooterState";

interface Circle {
  circleId: string;
  name: string; // This becomes schoolName in schools-core
  description: string;
  isPublic: boolean; // Based on public/private setting
  memberCount: number;
  createdAt: string;
  createdBy: string;
  isSubCircle: boolean;
  parentCircle?: string; // Parent circle name if isSubCircle is true
  circleApproved: boolean; // Always true for sub-circles
  logo: string; // Uses main organization logo
  type: string; // Matches parent type
  learningPoints?: string[];
}

interface User {
  id: string;
  name: string;
  email: string;
  phoneNumber?: string;
}

export const CREATE_CIRCLE_FORM_ID = "create-circle-form";
export type { CreateCircleFormFooterState };

interface CreateCircleFormProps {
  onCircleCreated: (newCircle: Circle) => void;
  onCancel: () => void;
  existingCircles?: Circle[];
  organizationData: {
    schoolLogo: string;
    type: string;
  };
  /** When true, primary submit is rendered in ActionModal footer (mobile); tier-limit alert stays in the form. */
  anchorSubmitToFooter?: boolean;
  onFooterStateChange?: (state: CreateCircleFormFooterState) => void;
}

const CreateCircleForm: React.FC<CreateCircleFormProps> = ({
  onCircleCreated,
  onCancel,
  existingCircles = [],
  organizationData,
  anchorSubmitToFooter = false,
  onFooterStateChange,
}) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const { user, token } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    isPublic: false,
    logo: "",
    learningPoints: [] as string[],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [learningPointsError, setLearningPointsError] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [addMembersExpanded, setAddMembersExpanded] = useState(false);

  const [schoolUsers, setSchoolUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isImageSelectorOpen, setIsImageSelectorOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const loadSchoolUsers = useCallback(async () => {
    if (!user?.schoolId || !token) return;

    setIsLoadingUsers(true);
    try {
      const users = await api.users.getAll(user.schoolId, token);
      setSchoolUsers(users);
    } catch (error) {
      console.error("Error loading school users:", error);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [user?.schoolId, token]);

  useEffect(() => {
    loadSchoolUsers();
  }, [loadSchoolUsers]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLearningPointsError("");
    setDescriptionError("");

    if (!formData.name.trim()) {
      setError("Circle name is required");
      return;
    }

    const descriptionTrimmed = formData.description.trim();
    if (!descriptionTrimmed) {
      setDescriptionError(DESCRIPTION_REQUIRED_MESSAGE);
      return;
    }
    if (descriptionTrimmed.length > CIRCLE_DESCRIPTION_MAX_LENGTH) {
      setDescriptionError(DESCRIPTION_MAX_LENGTH_MESSAGE);
      return;
    }

    if (formData.learningPoints.length === 0) {
      setLearningPointsError(LEARNING_POINTS_REQUIRED_MESSAGE);
      return;
    }

    // Find the main circle (the one that's not a subcircle)
    const mainCircle = existingCircles.find((circle) => !circle.isSubCircle);
    if (!mainCircle) {
      setError("Main organization circle not found");
      return;
    }

    // Check sub-circle limit based on tier
    const existingSubCircles = existingCircles.filter(
      (circle) => circle.isSubCircle
    );
    const userTier = user?.tier || "0";
    const maxSubcircles = getMaxSubcircles(userTier);
    if (
      !isUnlimitedTierLimit(maxSubcircles) &&
      existingSubCircles.length >= maxSubcircles
    ) {
      setError(
        `Maximum ${maxSubcircles} sub-circles allowed for your subscription tier. You currently have ${existingSubCircles.length} sub-circles.`
      );
      return;
    }

    setIsSubmitting(true);

    try {
      if (!token || !user?.schoolId) {
        setError("Authentication required. Please log in again.");
        return;
      }

      // If a logo is selected, use it directly for the subcircle
      let logoUrl: string | undefined;
      if (formData.logo && formData.logo.trim() !== "") {
        logoUrl = formData.logo;
      }

      // Call the API to create subcircle
      const response = await api.circles.createSubCircle(
        {
          name: formData.name,
          description: descriptionTrimmed,
          isPublic: formData.isPublic,
          schoolId: user.schoolId,
          ...(logoUrl && { logo: logoUrl }),
          learningPoints: formData.learningPoints,
        },
        token
      );

      // Transform API response to match frontend interface
      const newCircle: Circle = {
        circleId: response.circle.circleId,
        name: response.circle.name,
        description: response.circle.description,
        isPublic: response.circle.isPublic,
        memberCount: response.circle.memberCount,
        createdAt: response.circle.createdAt,
        createdBy: response.circle.createdBy,
        isSubCircle: response.circle.isSubCircle,
        parentCircle: response.circle.parentCircle,
        circleApproved: response.circle.circleApproved,
        logo: response.circle.logo,
        type: response.circle.type,
        learningPoints: response.circle.learningPoints,
      };

      // Add selected users to the circle if any were selected
      if (selectedUsers.length > 0) {
        try {
          // Add users one by one to the circle
          for (const userToAdd of selectedUsers) {
            await api.circles.addUserToSubCircle(
              {
                email: userToAdd.email,
                phoneNumber: userToAdd.phoneNumber,
                subcircleId: response.circle.circleId,
                schoolId: user.schoolId,
              },
              token
            );
          }
        } catch (error) {
          console.error("Error adding users to circle:", error);
          // Don't fail the entire creation if adding users fails
          // The circle was created successfully, users can be added later
        }
      }

      onCircleCreated(newCircle);
    } catch (error: any) {
      console.error("Error creating subcircle:", error);
      setError(error.message || "Failed to create circle. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const existingSubCircles = existingCircles.filter(
    (circle) => circle.isSubCircle
  );

  // Check if user has reached tier limit
  const userTier = user?.tier || "0";
  const maxSubcircles = getMaxSubcircles(userTier);
  const hasReachedLimit =
    !isUnlimitedTierLimit(maxSubcircles) &&
    existingSubCircles.length >= maxSubcircles;
  const limitMessage = hasReachedLimit
    ? `You've reached your tier limit of ${maxSubcircles} sub-circles. Upgrade your plan to create more circles.`
    : "";

  useEffect(() => {
    if (!anchorSubmitToFooter || !onFooterStateChange) return;
    onFooterStateChange({
      isSubmitting,
      hasReachedLimit,
      selectedUsersCount: selectedUsers.length,
      limitMessage,
    });
  }, [
    anchorSubmitToFooter,
    isSubmitting,
    hasReachedLimit,
    selectedUsers.length,
    limitMessage,
    onFooterStateChange,
  ]);

  const sectionGap = anchorSubmitToFooter ? 2 : 3;
  /**
   * Desktop modal (md+): public toggle in the right rail (same flag name as EditCircleForm).
   * On create, learning points always stay on the left.
   */
  const learningPublicInRightPanel = anchorSubmitToFooter && isMdUp;

  const outlineFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 1,
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "divider",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: lightTheme.colors.materialBlue700,
        borderWidth: 1,
      },
    },
    "& .MuiInputLabel-root": {
      fontWeight: 500,
    },
  } as const;

  const learningPointsField = (
    <FormControl
      fullWidth
      size="medium"
      required
      error={Boolean(learningPointsError)}
    >
      <InputLabel id="create-learning-points-label">
        Learning points (max {CIRCLE_LEARNING_POINT_MAX})
      </InputLabel>
      <Select
        labelId="create-learning-points-label"
        multiple
        value={formData.learningPoints}
        onChange={(e) => {
          setLearningPointsError("");
          const next = (e.target.value as string[]).slice(
            0,
            CIRCLE_LEARNING_POINT_MAX
          );
          setFormData((prev) => ({ ...prev, learningPoints: next }));
        }}
        input={
          <OutlinedInput label={`Learning points (max ${CIRCLE_LEARNING_POINT_MAX})`} />
        }
        renderValue={(selected) =>
          (selected as string[])
            .map(
              (id) =>
                CIRCLE_LEARNING_POINT_OPTIONS.find((opt) => opt.id === id)?.label || id
            )
            .join(", ")
        }
        aria-describedby="create-learning-points-helper"
      >
        {CIRCLE_LEARNING_POINT_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.id}
            value={opt.id}
            disabled={
              !formData.learningPoints.includes(opt.id) &&
              formData.learningPoints.length >= CIRCLE_LEARNING_POINT_MAX
            }
          >
            <Checkbox checked={formData.learningPoints.includes(opt.id)} />
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Select>
      <FormHelperText id="create-learning-points-helper">
        {learningPointsError || LEARNING_POINTS_HELPER_TEXT}
      </FormHelperText>
    </FormControl>
  );

  const publicCircleToggle = (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
        p: anchorSubmitToFooter ? 1.5 : 2,
        bgcolor: lightTheme.colors.mobileNavBackground,
        borderRadius: 2,
        border: `1px solid ${lightTheme.colors.borderLight}`,
      }}
    >
      <Switch
        checked={formData.isPublic}
        onChange={(e) =>
          setFormData((prev) => ({ ...prev, isPublic: e.target.checked }))
        }
        color="primary"
        size="medium"
        sx={{
          "& .MuiSwitch-switchBase.Mui-checked": {
            color: theme.palette.common.white,
          },
          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            backgroundColor: theme.palette.primary.main,
            opacity: 1,
          },
        }}
      />
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="body1"
          fontWeight="600"
          color="text.primary"
          gutterBottom
        >
          Public Circle
        </Typography>
        <Typography variant="body2" color="text.secondary" lineHeight={1.5}>
          {formData.isPublic
            ? "Anyone in your organization can join this circle"
            : "Only invited members can join this circle"}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      component="form"
      id={CREATE_CIRCLE_FORM_ID}
      onSubmit={handleSubmit}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: anchorSubmitToFooter ? 2 : 3,
        p: anchorSubmitToFooter ? 0 : { xs: 1.5, sm: 3 },
        width: "100%",
        maxWidth: anchorSubmitToFooter ? "100%" : undefined,
        mx: anchorSubmitToFooter ? 0 : undefined,
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 2, md: 2 },
          width: "100%",
          alignItems: "stretch",
          flex: 1,
          minHeight: { md: 0 },
          ...(anchorSubmitToFooter && { pt: { xs: 1, sm: 1.5 } }),
        }}
      >
        {/* Left — circle details (matches EditCircleForm modal: flat, no inset card) */}
        <Box
          sx={{
            flex: { md: 1.2 },
            minWidth: 0,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: sectionGap,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "center", sm: "flex-start" },
              gap: 2,
              width: "100%",
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                display: "flex",
                justifyContent: { xs: "center", sm: "flex-start" },
                py: 0.5,
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.5,
                  transition: "all 0.2s ease",
                  "&:hover": { transform: "scale(1.02)" },
                }}
                onClick={() => setIsImageSelectorOpen(true)}
              >
                <Avatar
                  src={formData.logo || undefined}
                  sx={{
                    width: anchorSubmitToFooter ? 112 : 96,
                    height: anchorSubmitToFooter ? 112 : 96,
                    border: "3px dashed",
                    borderColor: "divider",
                    bgcolor: lightTheme.colors.mobileNavBackground,
                    fontSize: "2.35rem",
                    fontWeight: "bold",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: lightTheme.colors.materialBlue700,
                      bgcolor: lightTheme.colors.surfaceAliceBlue,
                      boxShadow: `0 4px 12px ${lightTheme.colors.blueTint15}`,
                    },
                  }}
                >
                  {!formData.logo &&
                    (formData.name.trim()
                      ? formData.name.charAt(0).toUpperCase()
                      : "?")}
                </Avatar>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  sx={{
                    fontWeight: 500,
                    cursor: "pointer",
                    "&:hover": { color: lightTheme.colors.materialBlue700 },
                  }}
                >
                  {formData.logo ? "Change logo" : "Add logo"}
                </Typography>
              </Box>
            </Box>
            <TextField
              label="Circle Name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              required
              fullWidth
              size="medium"
              variant="outlined"
              placeholder="e.g., Grade 5 Parents, Teaching Staff"
              sx={{
                ...outlineFieldSx,
                flex: 1,
                minWidth: 0,
                alignSelf: { xs: "stretch", sm: "center" },
              }}
            />
          </Box>

          <TextField
            label="Circle Description"
            value={formData.description}
            onChange={(e) => {
              setDescriptionError("");
              setFormData((prev) => ({
                ...prev,
                description: e.target.value,
              }));
            }}
            multiline
            rows={5}
            fullWidth
            required
            size="medium"
            variant="outlined"
            placeholder="Describe the purpose of this circle"
            error={Boolean(descriptionError)}
            helperText={
              descriptionError ||
              `${DESCRIPTION_HELPER_TEXT} ${formData.description.length}/${CIRCLE_DESCRIPTION_MAX_LENGTH}`
            }
            inputProps={{ maxLength: CIRCLE_DESCRIPTION_MAX_LENGTH }}
            sx={outlineFieldSx}
          />

          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", md: 440 },
              alignSelf: "flex-start",
            }}
          >
            {learningPointsField}
          </Box>
          {!learningPublicInRightPanel && publicCircleToggle}

          {hasReachedLimit && (
            <Alert
              severity="warning"
              sx={{
                backgroundColor: lightTheme.colors.warningSurface,
                border: `1px solid ${lightTheme.colors.amber500}`,
                "& .MuiAlert-icon": {
                  color: lightTheme.colors.warningTextDark,
                },
                "& .MuiAlert-message": {
                  color: lightTheme.colors.warningTextDark,
                },
              }}
              action={
                <NoticeboardButton
                  variant="primary"
                  size="small"
                  onClick={() => setIsUpgradeModalOpen(true)}
                  sx={{
                    bgcolor: theme.palette.secondary.main,
                    border: "none",
                    "&:hover": {
                      bgcolor: lightTheme.colors.accentHover,
                    },
                  }}
                >
                  Upgrade
                </NoticeboardButton>
              }
            >
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                Tier Limit Reached
              </Typography>
              <Typography variant="body2">
                You've reached your tier limit of {maxSubcircles} sub-circles.
                Upgrade your plan to create more circles.
              </Typography>
            </Alert>
          )}

          {!anchorSubmitToFooter && (
            <Box sx={{ pt: 1 }}>
              <Tooltip
                title={hasReachedLimit ? limitMessage : ""}
                arrow
                placement="top"
              >
                <span style={{ width: "100%", display: "block" }}>
                  <NoticeboardButton
                    type="submit"
                    variant="primary"
                    disabled={isSubmitting || hasReachedLimit}
                    fullWidth
                  >
                    {isSubmitting
                      ? "Creating..."
                      : `Create Sub-Circle${
                          selectedUsers.length > 0
                            ? ` with ${selectedUsers.length} Member${
                                selectedUsers.length === 1 ? "" : "s"
                              }`
                            : ""
                        }`}
                  </NoticeboardButton>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Right — public (md+) + member picker */}
        <Box
          sx={{
            flex: { md: 0.8 },
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            minHeight: { md: 0 },
            width: "100%",
          }}
        >
          <Box
            sx={{
              pl: { md: 2 },
              borderLeft: { md: "1px solid" },
              borderColor: { md: "divider" },
              position: { md: "sticky" },
              top: { md: 8 },
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: { md: 0 },
            }}
          >
            {learningPublicInRightPanel && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  mb: 2,
                }}
              >
                {publicCircleToggle}
              </Box>
            )}

            {isMdUp ? (
              <Typography
                variant="subtitle1"
                sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}
              >
                Add Members
              </Typography>
            ) : (
              <Box
                role="button"
                aria-expanded={addMembersExpanded}
                tabIndex={0}
                onClick={() => setAddMembersExpanded((open) => !open)}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter" || ev.key === " ") {
                    ev.preventDefault();
                    setAddMembersExpanded((open) => !open);
                  }
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  cursor: "pointer",
                  borderRadius: 1,
                  py: 1,
                  px: 0.5,
                  mx: -0.5,
                  mb: addMembersExpanded ? 0 : 0.5,
                  "&:focus-visible": {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    Add Members
                  </Typography>
                  {selectedUsers.length > 0 && (
                    <Chip
                      size="small"
                      color="primary"
                      label={`${selectedUsers.length} selected`}
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>
                <ExpandMoreIcon
                  aria-hidden
                  sx={{
                    flexShrink: 0,
                    color: "action.active",
                    transform: addMembersExpanded
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: theme.transitions.create("transform", {
                      duration: theme.transitions.duration.shorter,
                    }),
                  }}
                />
              </Box>
            )}

            <Collapse in={isMdUp || addMembersExpanded} timeout="auto">
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1.5, ...(!isMdUp ? { pt: 0.5 } : {}) }}
                >
                  Select users to add to this circle when it's created. You can
                  always add or remove members later.
                </Typography>

                <SearchField
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search users by name, email, or phone..."
                />

                <Box
                  sx={{
                    flex: { md: 1 },
                    overflowY: "auto",
                    minHeight: { md: 120 },
                    maxHeight: { xs: "min(45vh, 340px)", md: "none" },
                    mt: 2,
                  }}
                >
                  {isLoadingUsers ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: "center", py: 2 }}
                    >
                      Loading users...
                    </Typography>
                  ) : schoolUsers.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: "center", py: 2 }}
                    >
                      No users found
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        mx: { xs: -1.25, sm: 0 },
                      }}
                    >
                      {schoolUsers
                        .filter(
                          (user) =>
                            user.name
                              .toLowerCase()
                              .includes(searchQuery.toLowerCase()) ||
                            user.email
                              .toLowerCase()
                              .includes(searchQuery.toLowerCase()) ||
                            phoneMatchesSearch(user.phoneNumber, searchQuery)
                        )
                        .map((user) => (
                          <Box
                            key={user.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              p: 1.5,
                              border: `1px solid ${
                                selectedUsers.some(
                                  (selected) => selected.id === user.id
                                )
                                  ? "primary.main"
                                  : lightTheme.colors.buttonHover
                              }`,
                              borderRadius: 1,
                              width: "100%",
                              backgroundColor: selectedUsers.some(
                                (selected) => selected.id === user.id
                              )
                                ? lightTheme.colors.blue50
                                : "transparent",
                              "&:hover": {
                                backgroundColor: selectedUsers.some(
                                  (selected) => selected.id === user.id
                                )
                                  ? lightTheme.colors.blue50
                                  : lightTheme.colors.grey100,
                              },
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                mr: 1.5,
                                bgcolor: getAvatarColor(user.name, lightTheme),
                              }}
                            >
                              {getInitials(user.name)}
                            </Avatar>
                            <Box sx={{ flexGrow: 1, minWidth: 0, pr: 1 }}>
                              <Typography variant="body2" fontWeight="medium">
                                {user.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  fontSize: { xs: "0.72rem", sm: "0.75rem" },
                                }}
                              >
                                {user.email}
                                {user.phoneNumber && ` • ${user.phoneNumber}`}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const isSelected = selectedUsers.some(
                                  (selected) => selected.id === user.id
                                );
                                if (isSelected) {
                                  setSelectedUsers((prev) =>
                                    prev.filter((u) => u.id !== user.id)
                                  );
                                } else {
                                  setSelectedUsers((prev) => [...prev, user]);
                                }
                              }}
                              sx={{
                                flexShrink: 0,
                                ml: 0.5,
                                color: selectedUsers.some(
                                  (selected) => selected.id === user.id
                                )
                                  ? "error.main"
                                  : "primary.main",
                              }}
                            >
                              {selectedUsers.some(
                                (selected) => selected.id === user.id
                              ) ? (
                                <RemoveIcon />
                              ) : (
                                <AddIcon />
                              )}
                            </IconButton>
                          </Box>
                        ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </Collapse>
          </Box>
        </Box>
      </Box>

      <ProfileImageSelectorModal
        open={isImageSelectorOpen}
        onClose={() => setIsImageSelectorOpen(false)}
        onImageSelectBase64={(base64) => {
          setFormData((prev) => ({ ...prev, logo: base64 }));
        }}
        returnBase64={true}
        schoolId={user?.schoolId}
        token={token || undefined}
      />

      <ActionModal
        open={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        title="Upgrade Your Plan"
        subtitle="Contact Noticeboard support to upgrade your subscription"
        maxWidth="sm"
        headerStyle="light"
      >
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
            To upgrade your plan and create more circles, please contact
            Noticeboard support or your Noticeboard customer care
            representative.
          </Typography>
          <NoticeboardButton
            variant="primary"
            onClick={() => setIsUpgradeModalOpen(false)}
            fullWidth
          >
            Close
          </NoticeboardButton>
        </Box>
      </ActionModal>
    </Box>
  );
};

export default CreateCircleForm;
